import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';

interface Coupon {
  id: number;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'flat' | 'lifetime';
  discount_value: string;
  applicable_plans: string[] | null;
  expiry_date: string | null;
  max_uses: number | null;
  current_uses: number;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
}

const emptyForm = {
  code: '',
  description: '',
  discount_type: 'percentage' as 'percentage' | 'flat' | 'lifetime',
  discount_value: '',
  applicable_plans: [] as string[],
  expiry_date: '',
  max_uses: '',
  is_public: false,
  is_active: true,
};

export default function CouponsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data: coupons = [], isLoading } = useQuery<Coupon[]>({
    queryKey: ['/api/admin/coupons'],
    queryFn: () => apiRequest('/api/admin/coupons'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['/api/admin/coupons'] });

  const createMutation = useMutation({
    mutationFn: (body: typeof emptyForm) => apiRequest('/api/admin/coupons', { method: 'POST', body }),
    onSuccess: () => { toast({ title: 'Coupon created' }); invalidate(); setDialogOpen(false); },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: typeof emptyForm }) =>
      apiRequest(`/api/admin/coupons/${id}`, { method: 'PUT', body }),
    onSuccess: () => { toast({ title: 'Coupon updated' }); invalidate(); setDialogOpen(false); },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/admin/coupons/${id}/toggle`, { method: 'PATCH' }),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/admin/coupons/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast({ title: 'Coupon deleted', variant: 'destructive' }); invalidate(); },
  });

  const openCreate = () => {
    setEditingCoupon(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c: Coupon) => {
    setEditingCoupon(c);
    setForm({
      code: c.code,
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      applicable_plans: c.applicable_plans || [],
      expiry_date: c.expiry_date ? c.expiry_date.split('T')[0] : '',
      max_uses: c.max_uses?.toString() || '',
      is_public: c.is_public,
      is_active: c.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const body = {
      ...form,
      discount_value: parseFloat(form.discount_value),
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      expiry_date: form.expiry_date || null,
      applicable_plans: form.applicable_plans.length ? form.applicable_plans : null,
    } as any;

    if (editingCoupon) {
      updateMutation.mutate({ id: editingCoupon.id, body });
    } else {
      createMutation.mutate(body);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <SuperAdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="h-6 w-6 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold">Coupons</h1>
              <p className="text-sm text-muted-foreground">Manage discount codes for subscription plans</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-2" /> Create Coupon
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">All Coupons ({coupons.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Plans</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Public</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</TableCell>
                  </TableRow>
                ) : coupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No coupons yet. Create one to get started.</TableCell>
                  </TableRow>
                ) : coupons.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono font-bold">{c.code}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.description || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {c.discount_type === 'percentage'
                          ? `${c.discount_value}% off`
                          : c.discount_type === 'lifetime'
                            ? `$${c.discount_value} lifetime`
                            : `$${c.discount_value} off`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.applicable_plans?.length ? c.applicable_plans.join(', ') : 'All plans'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.current_uses}{c.max_uses ? ` / ${c.max_uses}` : ''}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={c.is_public ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                        {c.is_public ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={c.is_active}
                        onCheckedChange={() => toggleMutation.mutate(c.id)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete coupon "{c.code}"?</AlertDialogTitle>
                              <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteMutation.mutate(c.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Code *</Label>
                <Input
                  placeholder="e.g. SUMMER20"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-1">
                <Label>Discount Type *</Label>
                <Select
                  value={form.discount_type}
                  onValueChange={v => setForm({
                    ...form,
                    discount_type: v as any,
                    applicable_plans: v === 'lifetime' ? ['single'] : form.applicable_plans,
                  })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat ($)</SelectItem>
                    <SelectItem value="lifetime">Lifetime deal ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>{form.discount_type === 'lifetime' ? 'One-time Price ($) *' : 'Discount Value *'}</Label>
                <Input
                  type="number"
                  placeholder={form.discount_type === 'percentage' ? '20' : form.discount_type === 'lifetime' ? '79' : '10'}
                  value={form.discount_value}
                  onChange={e => setForm({ ...form, discount_value: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Max Uses (optional)</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={form.max_uses}
                  onChange={e => setForm({ ...form, max_uses: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Description</Label>
              <Input
                placeholder="Summer sale discount"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Applicable Plans (optional)</Label>
                <Select
                  value={form.applicable_plans[0] || 'all'}
                  onValueChange={v => setForm({ ...form, applicable_plans: v === 'all' ? [] : [v] })}
                  disabled={form.discount_type === 'lifetime'}
                >
                  <SelectTrigger><SelectValue placeholder="All plans" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All plans</SelectItem>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
                {form.discount_type === 'lifetime' && (
                  <p className="text-xs text-muted-foreground">Lifetime deals are limited to the Single plan</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Expiry Date (optional)</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={e => setForm({ ...form, expiry_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_public}
                  onCheckedChange={v => setForm({ ...form, is_public: v })}
                />
                <Label>Show publicly on pricing page</Label>
              </div>
              {editingCoupon && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={v => setForm({ ...form, is_active: v })}
                  />
                  <Label>Active</Label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isPending || !form.code || !form.discount_value}>
              {isPending ? 'Saving…' : editingCoupon ? 'Save Changes' : 'Create Coupon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
